import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { Button, Card } from '@material-ui/core';
import { groupBy, mapValues, sortBy } from 'lodash';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Link } from 'react-router-dom';
import { ButtonWithPromise } from '../../../../../../../components/ButtonWithPromise';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { Section } from '../../../../../../../layout/Section';
import { batchSetDocs } from '../../../../../../../services/firebase';
import { callFirebaseFunction } from '../../../../../../../services/firebaseFunctions';
import { toMoment } from '../../../../../../../services/time';
import { SafeExecuteButton } from '../../../../../../components/SafeExecuteButton';
import { publishTopic } from '../../../../../../services/pubsub';
import {
  createSampleUsers,
  generatedUsersToUsers
} from '../../../../../../services/user';
import { Generator } from '../../../../../../services/user/generator';

type Props = {
  org: Doc<IOrganization>;
};

export const RepairInvestigate: React.FC<Props> = ({ org }) => {
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Section>
      <CanvasBar>
        <div>Repair/investigate</div>
      </CanvasBar>

      <Card>
        <FlexContainer wrap="wrap">
          <Link to={`/orgs/${org.id}/schedules-hanging`}>
            <Button variant="contained" color="primary">
              Show hanging schedules
            </Button>
          </Link>

          <SafeExecuteButton
            variant="contained"
            color="primary"
            onClick={async (execute) => {
              const result = await callFirebaseFunction(
                'schedules-_skipAllSchedulesThatAreAlreadyDone',
                {
                  orgId: org.id,
                  execute
                }
              );
              console.log(result);
            }}
          >
            Mark future schedules for done tasks as skipped
          </SafeExecuteButton>

          <ButtonWithPromise
            variant="contained"
            color="primary"
            pending="Initiating..."
            onClick={() =>
              publishTopic({
                topic: 'exp-experiences-checkForEnrollmentsInOrg',
                payload: { orgId: org.id, execute: false }
              }).then(
                () => {
                  enqueueSnackbar('Checking for enrollments in the background', {
                    variant: 'success'
                  });
                },
                () => {
                  enqueueSnackbar('Something went wrong', {
                    variant: 'error'
                  });
                }
              )
            }
          >
            Create recurring enrollments
          </ButtonWithPromise>

          <SafeExecuteButton
            variant="contained"
            color="primary"
            onClick={async (execute) => {
              const generator = new Generator({
                hireDate: {
                  min: moment('2020-01-01', 'YYYY-MM-DD').toDate(),
                  max: moment('2020-12-31', 'YYYY-MM-DD').toDate()
                },
                cities: ['San Francisco', 'Las Vegas'],
                departments: [
                  {
                    name: 'Engineering',
                    titles: ['Engineer', 'Designer'],
                    teams: ['Alpha', 'Beta']
                  },
                  {
                    name: 'Sales & Marketing',
                    titles: ['Marketeer', 'Sales Specialist'],
                    teams: ['Sigma', 'Tau']
                  },
                  {
                    name: 'HR',
                    titles: ['HR Business Partner'],
                    teams: ['People Team']
                  }
                ]
              });
              const samples = await generator.generateHierarchy(20);
              const users = generatedUsersToUsers(org.id, samples);
              console.log(users);
              if (execute) {
                await batchSetDocs(users);
              }
            }}
          >
            Generate sample users
          </SafeExecuteButton>

          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              createSampleUsers('xxx', 1000).then((users) => {
                console.log(users);
                const hireDates = sortBy(users, (u) =>
                  u.data.hireDate?.toMillis()
                ).map((u) => {
                  return u.data.hireDate
                    ? toMoment(u.data.hireDate).format('YYYY/MM/DD')
                    : '';
                });
                const grouped = mapValues(
                  groupBy(hireDates, (x) => x),
                  (x) => x.length
                );
                console.log(grouped);
              })
            }
          >
            Test sample users
          </Button>
        </FlexContainer>
      </Card>
    </Section>
  );
};
